import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  notification,
  Row,
  Select,
} from 'antd';
import * as API from 'aws-amplify/api';
import { ConsoleLogger as Logger } from 'aws-amplify/utils';
import { withTranslation } from 'react-i18next';

import './newDeliveryRelationship.less';

import moment from 'moment';
import Context from '../../context';
import {
  isOverlappingWithExistingDeliveryRelationship,
  isBeforeAllowedStartDate,
} from '../../utils/deliveryRelationshipHelpers';
import { localeDateFormat } from '../../utils/i18n';
import { isShipper } from '../../utils/marketparty';
import { createErrorMessage } from '../../context/globalStateHelper';

const logger = new Logger('new-delivery-relation-ship:form');


const NewDeliveryRelationship = (props) => {
  const { t } = props;
  const context = useContext(Context);
  const [isSending, setIsSending] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    const { deliveryRelationships, updateDeliveryRelationships } = context;

    if (deliveryRelationships.length === 0) {
      updateDeliveryRelationships();
    }
  }, []);

  /**
   * Check if date already booked.
   *
   * @param {Object} timestamp - Moment datetime.
   *
   * @returns {Boolean} - Result of checking.
   */
  const isDateAlreadyBooked = (timestamp) => {
    const { deliveryRelationships } = context;
    const { meteringSiteId } = props;

    return isBeforeAllowedStartDate(timestamp)
    || isOverlappingWithExistingDeliveryRelationship(
      timestamp,
      deliveryRelationships,
      meteringSiteId,
    );
  };

  /**
   * Checks if start date selected.
   *
   * @return {Boolean} - Result of check.
   */
  const isStartDateEmpty = () => {
    const value = Form.useWatch('start', form);
    return value === undefined || value === null;
  };

  /**
   * Render shippers data
   *
   * @returns {JSX} - Rendered part of the form.
   */
  const renderShippers = () => {
    const { marketPartyIndex } = context;

    return (
      <Form.Item
        label="Shipper"
        data-testid="new-delivery-relationship__form__shipper__select"
        colon={false}
        name="marketPartyId"
        rules={[{ required: true, message: t('common.form.emptyInputFieldErrorMessage') }]}
      >
        <Select
          placeholder="Choose shipper"
          disabled={isStartDateEmpty()}
        >
          { marketPartyIndex.filter(isShipper).map(party => (
            <Select.Option
              value={party.id}
              key={party.id}
            >
              {party.name}
            </Select.Option>
          )) }
        </Select>
      </Form.Item>
    );
  };

  const renderStartDate = () => (
    <Form.Item
      label="Start date"
      colon={false}
      name="start"
      rules={[{ required: true, message: t('common.form.dateErrorMessage') }]}
    >
      <DatePicker
        className="delivery-relationship__start-date__input"
        format={localeDateFormat()}
        disabledDate={isDateAlreadyBooked}
        data-testid="delivery-relationship__start-date__input"
        defaultPickerValue={moment()}
      />
    </Form.Item>
  );

  const renderEndDate = () => (
    <Form.Item
      label="End date"
      colon={false}
      name="end"
      rules={[{ required: true, message: t('common.form.dateErrorMessage') }]}
    >
      <DatePicker
        className="delivery-relationship__end-date__input"
        format={localeDateFormat()}
        disabledDate={isDateAlreadyBooked}
        disabled={isStartDateEmpty()}
        data-testid="delivery-relationship__end-date__input"
      />
    </Form.Item>
  );

  /**
   * Render title of component.
   *
   * @returns {JSX} - Rendered part.
   */
  const renderHeader = () => (
    <h3 className="new-delivery-relationship__form__header-text">
      { t('deliveryRelationship.new.title') }
    </h3>
  );

  const handleOnFinish = async (body) => {
    const { selectedMarketPartyId: marketPartyId } = context;
    const { meteringSiteId } = props;
    if (marketPartyId !== undefined) {
      setIsSending(true);

      try {
        const dateFormatAcceptedByBE = 'YYYY-MM-DD';
        await API.post({
          apiName: 'FINTSO',
          path: `/marketparties/${marketPartyId}/deliveryrelationships`,
          options: {
            body: {
              ...body,
              start: body.start.format(dateFormatAcceptedByBE),
              end: body.end.format(dateFormatAcceptedByBE),
              meteringSiteId,
            },
          },
        }).response;

        // Display notification to user
        notification.success({
          message: `${t('deliveryRelationship.new.title')} ${t('common.status.successfullyCreated')}`,
          description: '',
        });
        form.resetFields();
        setIsSending(false);
      } catch (err) {
        notification.error({
          className: 'notification-error',
          message: `${t('common.notifications.errorCreating')} ${t('deliveryRelationship.new.title').toLowerCase()}`,
          // TODO: translatable error message
          description: createErrorMessage(err),
        });
        logger.error(createErrorMessage(err, true));
        setIsSending(false);
      }
    }
  };

  const isButtonDisabled = () => {
    const start = Form.useWatch('start', form);
    const end = Form.useWatch('end', form);
    const marketPartyId = Form.useWatch('marketPartyId', form);

    // TODO: `end` field check should be deleted (not mandatory field)!
    return start === undefined || end === undefined || marketPartyId === undefined;
  };

  return (
    <>
      { renderHeader() }
      <Form
        className="new-delivery-relationship__form"
        onFinish={handleOnFinish}
        form={form}
        layout="vertical"
      >
        <Row align="middle" justify="start">
          <Col span={5}>
            { renderStartDate() }
          </Col>
          <Col span={1}>
            <div className="delivery-relationship__input-divider">
              -
            </div>
          </Col>
          <Col span={5}>
            { renderEndDate() }
          </Col>
          <Col span={1} />
          <Col span={12}>
            { renderShippers() }
          </Col>
        </Row>
        <Form.Item>
          <Row align="middle" justify="start" className="delivery-relationship__form__action-row">
            <Col span={7}>
              <Button
                type="primary"
                htmlType="submit"
                disabled={isButtonDisabled()}
                loading={isSending}
                block
              >
                { isSending ? `${t('common.status.wait')}...` : t('deliveryRelationship.new.submit.button.title') }
              </Button>
            </Col>
            <Col span={10} offset={1}>
              { t('deliveryRelationship.new.submit.action.description') }
            </Col>
          </Row>
        </Form.Item>
      </Form>
      <Divider />
    </>
  );
};

NewDeliveryRelationship.displayName = 'NewDeliveryRelationship';
NewDeliveryRelationship.contextType = Context;
NewDeliveryRelationship.propTypes = {
  meteringSiteId: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired,
};

export default withTranslation()(NewDeliveryRelationship);
export {
  NewDeliveryRelationship as PureComponent,
};
